import { useNavigate } from 'react-router-dom';
import { useModules } from '../lib/modules-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, LineChart, TrendingUp, HardHat, Sparkles, ArrowRight, Building2, Users, FolderKanban, Layers, Zap, Bot } from 'lucide-react';
import { motion } from 'framer-motion';

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: '$99',
    period: 'USD / mes',
    description: 'Para analizar proyectos individuales',
    color: 'var(--text-secondary)',
    modules: [{ name: 'Financial Intelligence', icon: LineChart, included: true }],
    limits: [
      { icon: FolderKanban, text: '3 proyectos' },
      { icon: Layers, text: '3 escenarios / proyecto' },
      { icon: Users, text: '1 usuario' },
    ],
    features: ['Análisis financiero completo', 'TIR, VAN, ROI, MOIC', 'Flujo de caja mensual', 'Sensibilidad básica', 'Reportes PDF', 'Copilot básico'],
    cta: 'Comenzar',
    highlighted: false,
  },
  {
    key: 'professional',
    name: 'Professional',
    price: '$249',
    period: 'USD / mes',
    description: 'Para desarrolladores con múltiples proyectos',
    color: 'var(--accent-primary)',
    modules: [
      { name: 'Financial Intelligence', icon: LineChart, included: true },
      { name: 'Market Intelligence', icon: TrendingUp, included: true },
    ],
    limits: [
      { icon: FolderKanban, text: '15 proyectos' },
      { icon: Layers, text: '10 escenarios / proyecto' },
      { icon: Users, text: '5 usuarios' },
    ],
    features: ['Todo en Starter', 'Comparables y posicionamiento', 'Pricing optimizer', 'Market Score y Project Score', 'Extractor de texto con IA', 'Copilot avanzado', 'Análisis de tornado'],
    cta: 'Elegir Professional',
    highlighted: true,
    badge: 'Recomendado',
  },
  {
    key: 'business',
    name: 'Business',
    price: '$499',
    period: 'USD / mes',
    description: 'Para equipos de desarrollo profesional',
    color: 'var(--construction-primary)',
    modules: [
      { name: 'Financial Intelligence', icon: LineChart, included: true },
      { name: 'Market Intelligence', icon: TrendingUp, included: true },
      { name: 'Construction Intelligence', icon: HardHat, included: true },
    ],
    limits: [
      { icon: FolderKanban, text: '50 proyectos' },
      { icon: Layers, text: '20 escenarios / proyecto' },
      { icon: Users, text: '10 usuarios' },
    ],
    features: ['Todo en Professional', 'Control de avance de obra', 'Presupuesto vs real', 'Alertas de desviación', 'Decision Engine', 'Scenario Strategy Engine'],
    cta: 'Elegir Business',
    highlighted: false,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'Sin límites para organizaciones grandes',
    color: 'var(--text-primary)',
    modules: [
      { name: 'Financial Intelligence', icon: LineChart, included: true },
      { name: 'Market Intelligence', icon: TrendingUp, included: true },
      { name: 'Construction Intelligence', icon: HardHat, included: true },
    ],
    limits: [
      { icon: FolderKanban, text: 'Proyectos ilimitados' },
      { icon: Layers, text: 'Escenarios ilimitados' },
      { icon: Users, text: 'Usuarios ilimitados' },
    ],
    features: ['Todo en Business', 'Multi-empresa', 'API de integración', 'White label', 'Soporte dedicado', 'SLA personalizado'],
    cta: 'Contactar ventas',
    highlighted: false,
  },
];

const ADDONS = [
  { key: 'market', name: 'Market Intelligence', icon: TrendingUp, color: 'var(--market-primary)', light: 'var(--market-light)', price: '$79', period: '/mes', desc: 'Comparables, posicionamiento, pricing optimizer, extractor IA.' },
  { key: 'construction', name: 'Construction Intelligence', icon: HardHat, color: 'var(--construction-primary)', light: 'var(--construction-light)', price: '$79', period: '/mes', desc: 'Avance de obra, presupuesto vs real, alertas de desviación.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { plan: currentPlan } = useModules();

  return (
    <div className="h-full overflow-y-auto" data-testid="pricing-page">
      <div className="px-6 lg:px-8 py-8 max-w-[1200px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2" style={{ color: 'var(--text-primary)' }}>Planes y precios</h1>
            <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Elige el plan que mejor se adapte a tu operación. Todos incluyen Financial Intelligence.
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {PLANS.map((p, i) => {
              const isCurrent = currentPlan?.key === p.key;
              return (
                <motion.div key={p.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                  <Card className={`rounded-xl shadow-sm h-full flex flex-col relative ${p.highlighted ? 'ring-2' : ''}`} style={{ borderColor: p.highlighted ? 'var(--accent-primary)' : 'var(--border-default)', ringColor: p.highlighted ? 'var(--accent-primary)' : undefined }}>
                    {p.badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="text-[10px] px-3 py-0.5 text-white shadow-sm" style={{ background: 'var(--accent-primary)' }}>{p.badge}</Badge>
                      </div>
                    )}
                    <CardContent className="p-5 flex flex-col flex-1">
                      {/* Name + price */}
                      <div className="mb-4">
                        <h3 className="text-base font-display font-bold mb-1" style={{ color: p.color }}>{p.name}</h3>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-display font-bold" style={{ color: 'var(--text-primary)' }}>{p.price}</span>
                          {p.period && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.period}</span>}
                        </div>
                        <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{p.description}</p>
                      </div>

                      {/* Modules */}
                      <div className="mb-4">
                        <div className="text-[9px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Módulos incluidos</div>
                        <div className="space-y-1">
                          {p.modules.map(m => (
                            <div key={m.name} className="flex items-center gap-1.5">
                              <m.icon className="w-3 h-3" style={{ color: m.included ? 'var(--success)' : 'var(--text-muted)' }} />
                              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{m.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Limits */}
                      <div className="mb-4">
                        <div className="text-[9px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Capacidad</div>
                        <div className="space-y-1">
                          {p.limits.map((l, li) => (
                            <div key={li} className="flex items-center gap-1.5">
                              <l.icon className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{l.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Features */}
                      <div className="flex-1 mb-5">
                        <div className="text-[9px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Funcionalidades</div>
                        <div className="space-y-1">
                          {p.features.map((f, fi) => (
                            <div key={fi} className="flex items-start gap-1.5">
                              <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" style={{ color: 'var(--success)' }} />
                              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{f}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      {isCurrent ? (
                        <Button disabled className="w-full h-10 text-xs" variant="outline">Plan actual</Button>
                      ) : (
                        <Button
                          className={`w-full h-10 text-xs font-semibold ${p.highlighted ? 'text-white' : ''}`}
                          variant={p.highlighted ? 'default' : 'outline'}
                          style={p.highlighted ? { background: 'var(--accent-primary)' } : { borderColor: p.color, color: p.color }}
                          onClick={() => navigate('/settings')}
                        >
                          {p.cta} {p.key !== 'enterprise' && <ArrowRight className="ml-1 w-3 h-3" />}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Add-ons section */}
          <div className="mb-8">
            <div className="text-center mb-6">
              <h2 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Módulos adicionales</h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Agrega módulos a cualquier plan para expandir las capacidades de tu plataforma.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {ADDONS.map(addon => {
                const Icon = addon.icon;
                return (
                  <Card key={addon.key} className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: addon.light }}>
                        <Icon className="w-5 h-5" style={{ color: addon.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{addon.name}</span>
                          <Badge className="text-[8px]" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)', border: 'none' }}>Add-on</Badge>
                        </div>
                        <p className="text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>{addon.desc}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{addon.price}</span>
                          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{addon.period}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* FAQ / Bottom note */}
          <div className="text-center pt-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Todos los planes incluyen trial de 30 días con todos los módulos activos. Sin compromiso.
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              ¿Necesitas un plan personalizado? <button className="font-semibold transition-colors hover:underline" style={{ color: 'var(--accent-primary)' }}>Contáctanos</button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
